import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { logo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  SiteSettingsEditor, HomeEditor, PagesEditor, AreasEditor, ListsEditor, CareersEditor
} from '../components/admin/editors';
import Submissions from '../components/admin/Submissions';
import EmailSettings from '../components/admin/EmailSettings';
import Seo from '../components/Seo';
import {
  Settings, Home as HomeIcon, FileText, MapPin, Users, Inbox,
  Save, LogOut, Lock, ExternalLink, RotateCcw, Loader2, Briefcase, Mail
} from 'lucide-react';

const TABS = [
  { key: 'settings', label: 'Site Settings', icon: Settings },
  { key: 'home', label: 'Home Page', icon: HomeIcon },
  { key: 'pages', label: 'Service Pages', icon: FileText },
  { key: 'areas', label: 'Service Areas', icon: MapPin },
  { key: 'lists', label: 'Team / Photos', icon: Users },
  { key: 'careers', label: 'Careers Page', icon: Briefcase },
  { key: 'submissions', label: 'Submissions', icon: Inbox },
  { key: 'email', label: 'Email Notifications', icon: Mail }
];

// ---------- Login ----------
const Login = ({ onLogin }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/login', form);
      localStorage.setItem('comfortair_token', r.data.access_token);
      onLogin(r.data.username);
    } catch (err) {
      toast({ title: 'Login failed', description: 'Invalid username or password.', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-brand-bluedeep flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg px-5 py-4 inline-block mx-auto mb-6 shadow-lg w-full text-center">
          <img src={logo} alt="Comfort Air" className="h-12 mx-auto" />
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-8 space-y-5">
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-brand-bluelight flex items-center justify-center mx-auto mb-3">
              <Lock className="text-brand-blue" size={26} />
            </div>
            <h1 className="font-display font-extrabold text-2xl text-brand-bluedark">CMS Admin Login</h1>
            <p className="text-slate-500 text-sm mt-1">Sign in to manage your website content.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Username</Label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="admin" required />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" required />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-brand-green hover:bg-brand-greendark h-11 font-semibold gap-2">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={17} />} Sign In
          </Button>
        </form>
      </div>
    </div>
  );
};

// ---------- Dashboard ----------
const Dashboard = ({ username, onLogout }) => {
  const { toast } = useToast();
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState('settings');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/content').then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const change = (next) => { setContent(next); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/content', { content });
      setDirty(false);
      toast({ title: 'Saved!', description: 'Your changes are now live on the website.' });
    } catch (e) {
      toast({ title: 'Save failed', description: 'Please try again.', variant: 'destructive' });
    }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm('Reset ALL content to the original defaults? This cannot be undone.')) return;
    const r = await api.post('/content/reset');
    setContent(r.data.data || r.data);
    setDirty(false);
    toast({ title: 'Content reset', description: 'Default content restored.' });
  };

  if (!content) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading content…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Top bar */}
      <header className="bg-brand-bluedeep text-white sticky top-0 z-30 shadow-lg">
        <div className="px-4 md:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-md px-2.5 py-1.5"><img src={logo} alt="Comfort Air" className="h-6" /></div>
            <span className="font-display font-bold hidden sm:block">Content Manager</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white gap-1"><ExternalLink size={15} /> <span className="hidden sm:inline">View Site</span></Button>
            </a>
            <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-brand-green hover:bg-brand-greendark gap-1">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} {dirty ? 'Save Changes' : 'Saved'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-white/80 hover:text-white hover:bg-white/10 gap-1"><LogOut size={15} /></Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-16 md:w-60 bg-white border-r border-slate-200 shrink-0">
          <nav className="p-2 md:p-4 space-y-1 sticky top-16">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${tab === t.key ? 'bg-brand-bluelight text-brand-blue' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <Icon size={18} className="shrink-0" /> <span className="hidden md:inline">{t.label}</span>
                </button>
              );
            })}
            <button onClick={reset} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-red-500 hover:bg-red-50 mt-4">
              <RotateCcw size={18} className="shrink-0" /> <span className="hidden md:inline">Reset Defaults</span>
            </button>
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display font-extrabold text-2xl text-brand-bluedark">{TABS.find((t) => t.key === tab)?.label}</h2>
              <span className="text-sm text-slate-400">Signed in as <b className="text-slate-600">{username}</b></span>
            </div>
            {tab === 'settings' && <SiteSettingsEditor content={content} onChange={change} />}
            {tab === 'home' && <HomeEditor content={content} onChange={change} />}
            {tab === 'pages' && <PagesEditor content={content} onChange={change} />}
            {tab === 'areas' && <AreasEditor content={content} onChange={change} />}
            {tab === 'lists' && <ListsEditor content={content} onChange={change} />}
            {tab === 'careers' && <CareersEditor content={content} onChange={change} />}
            {tab === 'submissions' && <Submissions />}
            {tab === 'email' && <EmailSettings />}
          </div>
        </main>
      </div>
    </div>
  );
};

// ---------- Admin root ----------
const Admin = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('comfortair_token');
    if (!token) { setChecking(false); return; }
    api.get('/auth/me').then((r) => setUser(r.data.username)).catch(() => {
      localStorage.removeItem('comfortair_token');
    }).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem('comfortair_token'); setUser(null); };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  return (
    <>
      <Seo title="CMS Admin | Comfort Air Mechanical" noindex path="/admin" />
      {user ? <Dashboard username={user} onLogout={logout} /> : <Login onLogin={setUser} />}
    </>
  );
};

export default Admin;
